<template>
  <ValidationObserver ref="form" v-slot="{}" tag="form" class="c-group" @submit.prevent="handleSubmit">
    <!-- details section -->
    <div class="c-group__section" v-if="tab === 'details'">
      <div class="c-group__form">
        <InputFile :file="currentFile" :multiple="false" :includeReader="true" @change="addFile" />

        <ValidationProvider v-slot="{ errors }" class="ui-group" rules="required">
          <Input
            :value="details.name"
            theme="dynamic"
            :label="$t('chat.create-group.name-input-label')"
            type="text"
            :error="errors[0]"
            @onChange="(v) => (details.name = v)"
          />
        </ValidationProvider>

        <ValidationProvider v-slot="{ errors }" class="ui-group">
          <Input
            textarea
            :value="details.description"
            theme="dynamic"
            :label="$t('chat.create-group.description-input-label')"
            rows="5"
            :error="errors[0]"
            @onChange="(v) => (details.description = v)"
          />
        </ValidationProvider>
      </div>
    </div>

    <!-- members section -->
    <div class="c-group__section" v-if="tab === 'members'">
      <div class="chat__members" v-for="group in membersGroupedWithSearch" :key="group.key">
        <div class="chat__members-key">{{ group.key }}</div>
        <Member
          v-for="member in group.list"
          :key="member.id"
          :data="member"
          :is-selectable="true"
          :selected="selectedMembers.includes(member.id)"
          @changeSelection="changeMemberSelection"
          no-actions
        />
      </div>
    </div>

    <div class="c-group__cta ttt">
      <button v-if="tab === 'details'" type="submit" class="blue large">{{ $t('next') }}</button>
      <button v-else type="submit" class="blue large">{{ $t('chat.create-group.create-btn') }}</button>
    </div>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { withUi } from '@/components/ui'
import InputFile from '@/components/form/InputFile'

import { obj2FormData } from '@/utils/formdata'

import Member from './Member'

export default {
  components: { InputFile, Member },
  mixins: [withUi],
  props: {
    activeDialog: {
      required: false,
      type: Object,
    },
    search: String,
    active: Boolean,
  },
  data: () => ({
    tab: 'details',
    details: {
      photo: null,
      name: null,
      description: null,
    },
    groupId: null,
    selectedMembers: [],
  }),
  computed: {
    membersGroupedWithSearch() {
      const members = this.membersGrouped
      const searchStr = this.search.trim().toLowerCase()

      if (members && members.length) {
        const mapped = members.map((group) => {
          return {
            key: group.key,
            list: group.list.filter((m) => {
              const matchFirstName = m.first_name && m.first_name.toLowerCase().indexOf(searchStr) !== -1
              const matchLastName = m.last_name && m.last_name.toLowerCase().indexOf(searchStr) !== -1

              return matchFirstName || matchLastName
            }),
          }
        })

        return mapped.filter((x) => x.list && x.list.length)
      }

      return []
    },
    currentFile() {
      return this.details.photo?.file ?? this.details.photo;
    },
    ...mapGetters('chat', ['isConnected', 'members', 'membersGrouped']),
  },
  methods: {
    resetState() {
      this.tab = 'details'
      this.groupId = null
      this.details = {
        photo: this.activeDialog?.avatar,
        name: this.activeDialog?.title,
        description: this.activeDialog?.description,
      }
      this.selectedMembers = []
      // reset memebers ?
    },
    changeMemberSelection(id) {
      if (!this.selectedMembers.includes(id)) {
        this.selectedMembers = [...this.selectedMembers, id]
      } else {
        this.selectedMembers = this.selectedMembers.filter((x) => x !== id)
      }
    },
    async handleSubmit(e) {
      if(this.tab === 'members') {
        return this.updateGroup({
          id: this.groupId,
          members: this.selectedMembers,
        }).then(() => {
          this.$emit('onSubmit', this.groupId)
          this.resetState()
        });
      }

      const isValid = await this.$refs.form.validate()
      if (!isValid) {
        return
      }

      const { name, description, photo } = this.details

      let postObj = {title: name, type: 3, description, members: this.selectedMembers }

      if (photo) {
        postObj = obj2FormData(postObj, new FormData())
        postObj.append('upload', photo.file)
      }
      postObj.id = this.activeDialog?.id;
      const methodName = this.activeDialog ? 'updateGroup' : 'createGroup';
      await this[methodName](postObj)
        .then((res) => {
          this.error = null
          this.groupId = res.id;
          if(this.activeDialog) {
            this.$emit('onSubmit', this.groupId)
            this.resetState()
          }
          else {
            this.tab = 'members';
          }
        })
        .catch((err) => {
          console.error(err);
          this.$toast({ type: 'error', title: err.message })
        })
    },
    addFile(file) {
      this.details.photo = {
        name: file.name,
        file,
      }
    },
    ...mapActions('chat', ['createGroup', 'updateGroup']),
  },
  watch: {
    active() {
      this.resetState();
    },
    activeDialog() {
      this.resetState();
    }
  }
}
</script>

<style lang="scss">
.c-group {
  flex: 1 0 auto;
  padding: 24px 24px 80px 24px;
  max-height: 100%;
  &__tabs {
    display: flex;
    margin: 20px 0 16px;
    list-style: none;
    padding: 7px 10px;
    background: #f7f7f7;
    border-radius: 5px;
    li {
      flex: 1 1 auto;
      border-radius: 5px;
      font-size: 14px;
      line-height: 17px;
      color: $dark;
      padding: 10px;
      text-align: center;
      cursor: pointer;
      transition: all 0.25s ease;
      &.is-active {
        font-weight: bold;
        background: #ffffff;
        color: $text;
        box-shadow: 0px 7px 27px 10px rgba(180, 187, 198, 0.15);
      }
    }
  }
  &__form {
    position: relative;
    .ui-group {
      display: block;
      margin-bottom: 16px;
    }
  }
  &__cta {
    position: fixed;
    z-index: 3;
    bottom: 32px;
    left: 36px;
    right: 36px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    button {
      flex: 1 1 auto;
      margin-right: 16px;
      margin-top: 10px;
      &:last-child {
        margin-right: 0;
      }
    }
  }

  &__section {
    overflow: auto;
    max-height: 100%;
  }
}
</style>
